import React, { useCallback, useEffect, useState } from 'react';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { translateText } from '../../lib/aiTranslate';
import { ageLabel, waitAge } from '../../lib/marketOrderAge';
import { adminConfirmPrice } from '../../lib/marketOrdersApi';
import { formatSar } from '../../lib/marketPricing';
import {
  adminCloseInquiry, adminInquiries, adminPostInquiry, adminSaveSupplierMeasures, fetchInquiryMessages, relayMessage, supplierCity, waitingQuestions,
} from '../../lib/marketInquiries';

// «اسأل المورد» — staff side (owner, 2026-10-30). Phase 1 is staff-relayed:
//   1. copy the trader's waiting questions, in Chinese, with the product link → HioBuy on WhatsApp;
//   2. post the supplier's answer back as the supplier (the trader is notified);
//   3. when the answer gives a weight or size, save it to the product (source 'supplier').
// Everything posted here is masked for contact details like the trader's side.
// The 1688 link is for staff only; it never reaches the trader.
const AGE_COLOR = { ok: undefined, amber: '#9A6B12', red: '#B3261E' };
const STATUS = { open: ['بانتظار الرد', 'Waiting'], answered: ['تمت الإجابة', 'Answered'], closed: ['مغلق', 'Closed'] };
const WHO = { trader: ['التاجر', 'Trader'], supplier: ['المورد', 'Supplier'], admin: ['ملاحظة الفريق', 'Team note'] };

export default function ConsoleMarketInquiries({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState('open');
  const [openId, setOpenId] = useState(null);
  const [toast, setToast] = useState('');
  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2600); };
  const load = useCallback(async () => { const r = await adminInquiries(filter === 'all' ? null : filter); setRows(r.ok ? r.data || [] : []); }, [filter]);
  useEffect(() => { load(); }, [load]);
  const open = (rows || []).find((r) => r.id === openId);

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="market-inquiries">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <div className="ac-page-head"><div>
          <h1 className="ac-h1">{isAr ? 'أسئلة التجار للموردين' : 'Trader questions to suppliers'}</h1>
          <p className="ac-sub">{isAr ? 'انسخ السؤال بالصينية لـ HioBuy على واتساب، ثم الصق رد المورد هنا. المورد يرد عادة خلال 24 ساعة — هذا ما يراه التاجر.' : 'Copy the question in Chinese to HioBuy on WhatsApp, then paste the supplier\'s answer here. Traders are told: usually within 24 hours.'}</p>
        </div></div>
        <div className="ac-chiprow" style={{ marginBottom: 12 }}>
          {[['open', 'بانتظار الرد', 'Waiting'], ['answered', 'تمت الإجابة', 'Answered'], ['all', 'الكل', 'All']].map(([k, ar, en]) => (
            <button key={k} data-inq-filter={k} className={`ac-chip${filter === k ? ' on' : ''}`} onClick={() => { setFilter(k); setOpenId(null); }}>{isAr ? ar : en}</button>
          ))}
        </div>
        {rows == null ? <div className="ac-skel" style={{ height: 80 }} /> : rows.length === 0 ? <div className="ac-placeholder">{isAr ? 'لا شيء هنا.' : 'Nothing here.'}</div> : (
          <div className="ac-tablewrap"><table className="ac-table">
            <thead><tr><th>{isAr ? 'المنتج' : 'Product'}</th><th>{isAr ? 'التاجر' : 'Trader'}</th><th>{isAr ? 'الحالة' : 'Status'}</th><th>{isAr ? 'ينتظر منذ' : 'Waiting'}</th><th>{isAr ? 'آخر رسالة' : 'Last'}</th></tr></thead>
            <tbody>{rows.map((r) => {
              const a = r.status === 'open' ? waitAge(r.waiting_since) : null;
              return (
                <React.Fragment key={r.id}>
                  <tr data-inquiry={r.id} onClick={() => setOpenId(openId === r.id ? null : r.id)} style={{ cursor: 'pointer' }}>
                    <td>{r.kind === 'price_request' && <span className="ac-badge warn" style={{ marginInlineEnd: 6 }}>{isAr ? 'تثبيت سعر' : 'Price'}</span>}{r.product_ref?.title_ar || r.product_ref?.title_en}</td><td>{r.trader_name || '—'}</td>
                    <td>{(STATUS[r.status] || [r.status, r.status])[isAr ? 0 : 1]}</td>
                    <td data-wait-hours={a ? a.hours : ''} style={{ fontWeight: 700, color: a ? AGE_COLOR[a.tone] : undefined }}>{a ? ageLabel(a.hours, isAr) : '—'}</td>
                    <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.last}</td>
                  </tr>
                  {openId === r.id && <tr><td colSpan={5}><InquiryPanel row={open} isAr={isAr} flash={flash} onChanged={load} /></td></tr>}
                </React.Fragment>
              );
            })}</tbody>
          </table></div>
        )}
      </div>
      {toast && <div className="ac-toast" data-toast="1">{toast}</div>}
    </ConsoleShell>
  );
}

// Rule C (owner, 2026-10-30/31): a cart waiting for its shipping price. Ask HioBuy for each product's
// weight and size, save them here (the product is corrected for the next trader), then price the
// order with the store's own math — the trader gets the final total and pays once within 48 hours.
function PriceRequestPanel({ row, isAr, flash, onChanged }) {
  const ord = row.order || {};
  const [m, setM] = useState({});
  const [direct, setDirect] = useState({ sar: '', method: 'sea', note: '' });   // a typed shipping price
  const [busy, setBusy] = useState('');
  const [res, setRes] = useState(null);
  const n = (v) => (v === '' || v == null ? null : Number(v));
  const save = async (l) => {
    const k = `${l.product_id}|${l.sku_key || ''}`; const x = m[k] || {};
    setBusy(k);
    const r = await adminSaveSupplierMeasures({ product_id: l.product_id, sku_key: l.sku_key || null, unit_kg: n(x.kg), length_cm: n(x.l), width_cm: n(x.w), height_cm: n(x.h) });
    setBusy('');
    const rej = r.ok ? (r.data?.rejected || []) : [];
    flash(!r.ok ? r.error : rej.length ? `${isAr ? 'رُفض' : 'Rejected'}: ${rej.map((z) => z.reason).join(', ')}` : (isAr ? 'حُفظ' : 'Saved'));
    onChanged();
  };
  const price = async (opts) => {
    setBusy('price');
    const r = await adminConfirmPrice(row.order_id, opts);
    setBusy('');
    if (r?.ok === false) {
      flash(r.error === 'still_needs_measures' ? (isAr ? 'منتج ما زال بلا وزن ومقاس حقيقيين — احفظهما أولاً، أو اكتب سعر الشحن مباشرة' : 'A product still has no real weight and size — save them first, or type the shipping price')
        : r.error === 'basis_note_required' ? (isAr ? 'اكتب أساس السعر (10 أحرف على الأقل)' : 'Write what the price rests on (10+ characters)') : r.error);
      return;
    }
    setRes(r); flash(isAr ? 'أُرسل السعر النهائي للتاجر' : 'Final price sent to the trader'); onChanged();
  };
  return (
    <div className="ac-card" data-price-request={row.order_id}>
      <div className="ac-card-title">{isAr ? 'طلب تثبيت سعر' : 'Price confirmation'} · <span dir="ltr">{ord.order_no}</span> · {ord.status}</div>
      {(ord.lines || []).map((l) => {
        const k = `${l.product_id}|${l.sku_key || ''}`; const x = m[k] || {};
        return (
          <div key={k} data-pr-line={l.product_id} style={{ borderTop: '1px solid #eee', padding: '8px 0', fontSize: 13 }}>
            <div><b>{l.title_ar}</b>{l.variant_ar ? ` · ${l.variant_ar}` : ''} ×{l.qty} · {formatSar(Number(l.unit_sar))}</div>
            <div style={{ color: '#555', fontSize: 12 }}>
              {isAr ? 'الحالي' : 'Now'}: {l.measures?.unit_kg ?? '—'} {isAr ? 'كجم' : 'kg'} · {l.measures?.cm3 ? `${Math.round(l.measures.cm3).toLocaleString('en-US')} cm³` : (isAr ? 'بلا مقاس' : 'no size')} · {l.measures?.source ?? '—'}
              {l.source_url && <> · <a href={l.source_url} target="_blank" rel="noreferrer" dir="ltr">1688</a></>}
            </div>
            {ord.status === 'awaiting_shipping_quote' && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }} dir="ltr">
                {[['kg', 'kg'], ['l', 'L cm'], ['w', 'W cm'], ['h', 'H cm']].map(([f, ph]) => (
                  <input key={f} className="ac-input" style={{ width: 76 }} inputMode="decimal" placeholder={ph} value={x[f] || ''} onChange={(e) => setM({ ...m, [k]: { ...x, [f]: e.target.value } })} />
                ))}
                <button className="ac-btn ac-btn-sm" disabled={busy === k || !(Number(x.kg) > 0)} onClick={() => save(l)}>{busy === k ? '…' : isAr ? 'حفظ' : 'Save'}</button>
              </div>
            )}
          </div>
        );
      })}
      {ord.status === 'awaiting_shipping_quote' ? (
        <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className="ac-btn ac-btn-sm ac-btn-primary" data-confirm-price="1" disabled={busy === 'price'} onClick={() => price({})}>{busy === 'price' ? '…' : isAr ? 'احسب السعر وأرسله للتاجر' : 'Price it and send to the trader'}</button>
          <button className="ac-btn ac-btn-sm" disabled={busy === 'price'} onClick={() => window.confirm(isAr ? 'التسعير بالجوي رغم أن المنتج ثقيل؟' : 'Price by air although the product is heavy?') && price({ method: 'air', allowAir: true })}>{isAr ? 'تسعير بالجوي' : 'Price by air'}</button>
        </div>
      ) : null}
      {ord.status === 'awaiting_shipping_quote' ? (
        // unified with staff shipping (owner, 2026-11-01 «وحدهم»): heavy equipment the measures
        // tool refuses (over 100 kg — the generator) is priced by typing the shipping itself.
        // The fee follows the store's tiers (market-checkout confirm_price); VAT on the fee.
        <div data-direct-price="1" style={{ marginTop: 12, paddingTop: 10, borderTop: '1px dashed #ddd' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>{isAr ? 'أو اكتب سعر الشحن مباشرة (للطلب كامل، شامل الجمارك)' : 'Or type the shipping price (whole order, customs included)'}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <input className="ac-input" data-direct-sar="1" style={{ width: 140 }} dir="ltr" inputMode="decimal" placeholder={isAr ? 'ر.س' : 'SAR'} value={direct.sar} onChange={(e) => setDirect({ ...direct, sar: e.target.value })} />
            <select className="ac-input" data-direct-method="1" style={{ width: 110 }} value={direct.method} onChange={(e) => setDirect({ ...direct, method: e.target.value })}>
              <option value="sea">{isAr ? 'بحري' : 'Sea'}</option><option value="air">{isAr ? 'جوي' : 'Air'}</option>
            </select>
          </div>
          <textarea className="ac-textarea" data-direct-note="1" rows={2} style={{ marginTop: 6 }} placeholder={isAr ? 'أساس السعر (إلزامي): عرض HioBuy، الوزن الفعلي، القناة…' : 'Basis (required): HioBuy quote, actual weight, channel…'} value={direct.note} onChange={(e) => setDirect({ ...direct, note: e.target.value })} />
          <button className="ac-btn ac-btn-sm ac-btn-primary" data-direct-send="1" style={{ marginTop: 6 }} disabled={busy === 'price' || !(Number(direct.sar) > 0) || direct.note.trim().length < 10}
            onClick={() => window.confirm(isAr ? `إرسال سعر شحن ${direct.sar} ر.س للتاجر؟ يُضاف له المنتج ورسوم الخدمة.` : `Send a shipping price of ${direct.sar} SAR? Goods and the service fee are added.`)
              && price({ shippingSar: Number(direct.sar), method: direct.method, note: direct.note.trim() })}>
            {busy === 'price' ? '…' : isAr ? 'أرسل السعر للتاجر' : 'Send the price to the trader'}</button>
        </div>
      ) : (
        <p style={{ fontSize: 13, margin: '8px 0 0' }}>{isAr ? 'السعر المرسل' : 'Price sent'}: <b>{ord.total_sar != null ? formatSar(Number(ord.total_sar)) : '—'}</b> · {ord.method}{ord.pay_by ? ` · ${isAr ? 'الدفع قبل' : 'pay by'} ${new Date(ord.pay_by).toLocaleString(isAr ? 'ar-SA' : 'en-GB')}` : ''}</p>
      )}
      {res && <p style={{ fontSize: 12.5, color: '#2E7D32' }}>{isAr ? 'جوي' : 'Air'} {res.options?.air != null ? formatSar(res.options.air) : '—'} · {isAr ? 'بحري' : 'Sea'} {res.options?.sea != null ? formatSar(res.options.sea) : '—'}</p>}
    </div>
  );
}

function InquiryPanel({ row, isAr, flash, onChanged }) {
  if (row?.kind === 'price_request') return <PriceRequestPanel row={row} isAr={isAr} flash={flash} onChanged={onChanged} />;
  return <QuestionPanel row={row} isAr={isAr} flash={flash} onChanged={onChanged} />;
}

function QuestionPanel({ row, isAr, flash, onChanged }) {
  const [msgs, setMsgs] = useState(null);
  const [xl, setXl] = useState({});
  const [relay, setRelay] = useState('');
  const [answer, setAnswer] = useState('');
  const [asNote, setAsNote] = useState(false);
  const [m, setM] = useState({ kg: '', l: '', w: '', h: '' });
  const [busy, setBusy] = useState('');
  const load = useCallback(async () => setMsgs(await fetchInquiryMessages(row.id)), [row.id]);
  useEffect(() => { load(); }, [load]);
  if (!row) return null;
  const pr = row.product_ref || {};

  const buildRelay = async () => {
    setBusy('relay');
    const qs = waitingQuestions(msgs || []);
    const zh = await Promise.all(qs.map(async (q) => (await translateText(q, 'zh')).translated || q));
    setRelay(relayMessage({ questionsZh: zh, sourceUrl: row.source_url, skuSpecId: row.sku_spec_id, title: pr.title_zh }));
    setBusy('');
  };
  const translate = async (msg) => { const r = await translateText(msg.content, isAr ? 'ar' : 'en'); setXl((x) => ({ ...x, [msg.id]: r.translated || '—' })); };
  const post = async () => {
    setBusy('post');
    const r = await adminPostInquiry(row.id, answer, asNote ? 'staff' : 'supplier');
    setBusy('');
    if (!r.ok) { flash(r.error); return; }
    setAnswer(''); flash(asNote ? (isAr ? 'أُضيفت الملاحظة' : 'Note added') : (isAr ? 'نُشر الرد — أُشعر التاجر' : 'Answer posted — trader notified'));
    load(); onChanged();
  };
  const saveMeasures = async () => {
    setBusy('m');
    const n = (v) => (v === '' ? null : Number(v));
    const r = await adminSaveSupplierMeasures({ product_id: row.product_id, sku_key: row.sku_key || null, unit_kg: n(m.kg), length_cm: n(m.l), width_cm: n(m.w), height_cm: n(m.h) });
    setBusy('');
    const rej = r.ok ? (r.data?.rejected || []) : [];
    if (!r.ok) flash(r.error);
    else if (rej.length) flash(`${isAr ? 'رُفض' : 'Rejected'}: ${rej.map((x) => x.reason).join(', ')}`);
    else { flash(isAr ? 'حُفظ وزن/مقاس المورد — يتصحّح سعر المنتج' : 'Supplier weight/size saved — the product price corrects itself'); setM({ kg: '', l: '', w: '', h: '' }); }
  };

  return (
    <div data-inquiry-panel={row.id} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14, padding: '8px 0' }}>
      <div className="ac-card">
        <div className="ac-card-title">{pr.title_ar || pr.title_en}</div>
        <p style={{ fontSize: 12.5, margin: '0 0 8px' }}>
          {pr.seller_name} · {supplierCity(pr.origin_region, isAr ? 'ar' : 'en') || pr.origin_region || '—'}
          {row.product_id && <> · <a href={`/market/${row.product_id}`} target="_blank" rel="noreferrer">{isAr ? 'صفحة المنتج' : 'Product page'}</a></>}
          {row.source_url && <> · <a href={row.source_url} target="_blank" rel="noreferrer" dir="ltr">1688</a></>}
          {row.sku_spec_id && <span dir="ltr"> · SKU {row.sku_spec_id}</span>}
        </p>
        {msgs == null ? <div className="ac-skel" style={{ height: 60 }} /> : msgs.map((x) => (
          <div key={x.id} data-inq-msg={x.sender_role} style={{ margin: '0 0 8px', padding: 8, borderRadius: 8, fontSize: 13,
            background: x.sender_role === 'trader' ? '#F4F6FB' : x.sender_role === 'supplier' ? '#FFF8E6' : '#F2EDE3' }}>
            <div style={{ fontSize: 11.5, color: '#666' }}>{(WHO[x.sender_role] || [x.sender_role, x.sender_role])[isAr ? 0 : 1]} · {new Date(x.created_at).toLocaleString(isAr ? 'ar-SA' : 'en-GB')}</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{x.content}</div>
            {xl[x.id] ? <div style={{ fontStyle: 'italic', color: '#555', marginTop: 4 }}>{xl[x.id]}</div>
              : <button className="ac-btn ac-btn-sm" style={{ marginTop: 4 }} onClick={() => translate(x)}>{isAr ? 'ترجمة' : 'Translate'}</button>}
          </div>
        ))}
      </div>
      <div className="ac-card">
        <div className="ac-card-title">{isAr ? '1. أرسل السؤال لـ HioBuy' : '1. Send the question to HioBuy'}</div>
        <button className="ac-btn ac-btn-sm" data-build-relay="1" disabled={busy === 'relay' || !msgs} onClick={buildRelay}>{busy === 'relay' ? '…' : isAr ? 'جهّز الرسالة بالصينية' : 'Prepare the message in Chinese'}</button>
        {relay && <>
          <textarea className="ac-textarea" data-relay-text="1" rows={8} value={relay} onChange={(e) => setRelay(e.target.value)} dir="ltr" />
          <button className="ac-btn ac-btn-sm" onClick={() => navigator.clipboard?.writeText(relay).then(() => flash(isAr ? 'نُسخت' : 'Copied'))}>{isAr ? 'نسخ' : 'Copy'}</button>
        </>}
        <div className="ac-card-title" style={{ marginTop: 14 }}>{isAr ? '2. انشر رد المورد' : '2. Post the supplier\'s answer'}</div>
        <textarea className="ac-textarea" data-answer="1" rows={4} placeholder={isAr ? 'الصق رد المورد كما وصل (يُترجم للتاجر تلقائيًا). أرقام التواصل تُحجب تلقائيًا.' : 'Paste the answer as received (translated for the trader automatically). Contact details are masked.'} value={answer} onChange={(e) => setAnswer(e.target.value)} />
        <label style={{ fontSize: 12.5, display: 'flex', gap: 6, margin: '4px 0' }}><input type="checkbox" checked={asNote} onChange={(e) => setAsNote(e.target.checked)} />{isAr ? 'ملاحظة من فريق معبر (ليست كلام المورد)' : 'A note from the MAABAR team (not the supplier\'s words)'}</label>
        <button className="ac-btn ac-btn-sm ac-btn-primary" data-post-answer="1" disabled={busy === 'post' || !answer.trim()} onClick={post}>{busy === 'post' ? '…' : isAr ? 'نشر' : 'Post'}</button>
        <div className="ac-card-title" style={{ marginTop: 14 }}>{isAr ? '3. إن ذكر الوزن أو المقاس' : '3. If it gives a weight or size'}</div>
        <p style={{ fontSize: 12, color: '#555', margin: '0 0 6px' }}>{row.sku_key ? (isAr ? 'للخيار الذي سأل عنه التاجر.' : 'For the variant the trader asked about.') : (isAr ? 'لكل خيارات المنتج.' : 'For every variant of the product.')} {isAr ? 'وزن القطعة الواحدة بالكيلو، والمقاس اختياري (الثلاثة معًا).' : 'Weight of one piece in kg; size optional (all three).'}</p>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }} dir="ltr">
          {[['kg', 'kg'], ['l', 'L cm'], ['w', 'W cm'], ['h', 'H cm']].map(([k, ph]) => (
            <input key={k} className="ac-input" data-measure={k} style={{ width: 80 }} inputMode="decimal" placeholder={ph} value={m[k]} onChange={(e) => setM({ ...m, [k]: e.target.value })} />
          ))}
        </div>
        <button className="ac-btn ac-btn-sm" data-save-measures="1" disabled={busy === 'm' || !(Number(m.kg) > 0) || !row.product_id} onClick={saveMeasures}>{busy === 'm' ? '…' : isAr ? 'حفظ في بيانات المنتج' : 'Save to the product'}</button>
        {row.status !== 'closed' && <div style={{ marginTop: 14 }}><button className="ac-btn ac-btn-sm" onClick={async () => { await adminCloseInquiry(row.id); onChanged(); }}>{isAr ? 'إغلاق المحادثة' : 'Close the conversation'}</button></div>}
      </div>
    </div>
  );
}
